// ---------------------------------------------------------------------------
// Short confirmations and warnings.
//
// A toast is for something that succeeded, or a small failure the person can
// ignore. Anything they must act on belongs inline in the form or in a dialog,
// not in a message that disappears after four seconds.
// ---------------------------------------------------------------------------

import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
  /// Optional second line, for example the reason something failed.
  detail?: string;
}

interface ToastState {
  toasts: Toast[];
  show: (tone: ToastTone, message: string, detail?: string) => void;
  dismiss: (id: string) => void;
}

/// Errors stay on screen twice as long, because they are worth reading.
const LIFETIME_MS: Record<ToastTone, number> = {
  success: 3500,
  info: 4000,
  error: 7000,
};

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  show: (tone, message, detail) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    set((state) => ({ toasts: [...state.toasts, { id, tone, message, detail }] }));

    setTimeout(() => get().dismiss(id), LIFETIME_MS[tone]);
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));

/// Convenience wrappers so call sites read as sentences.
export const toast = {
  success: (message: string, detail?: string) => useToastStore.getState().show('success', message, detail),
  error: (message: string, detail?: string) => useToastStore.getState().show('error', message, detail),
  info: (message: string, detail?: string) => useToastStore.getState().show('info', message, detail),
};
